import React from "react";

import { Columns3 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";
import { Separator } from "@/src/components/ui/separator";

import { CONTROL_SURFACE_CLASS, getColumnLabel } from "./dataTableStyles";

import type { RowData } from "@tanstack/react-table";
import type { JSX } from "react";

import type { DataTableColumn } from "./dataTable.types";

interface DataTableColumnVisibilityProps<TData extends RowData> {
  columns: DataTableColumn<TData>[];
}

function DataTableColumnVisibility<TData extends RowData>({
  columns,
}: DataTableColumnVisibilityProps<TData>): JSX.Element {
  const { t } = useTranslation("table");
  const hiddenCount = columns.filter((column) => !column.getIsVisible()).length;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className={CONTROL_SURFACE_CLASS} size="lg" variant="outline">
          <Columns3 />
          {t("dataTable.columns")}
          {hiddenCount > 0 ? (
            <React.Fragment>
              <Separator className="mx-1 h-4" orientation="vertical" />
              <Badge variant="secondary">
                {t("dataTable.hiddenColumns", { count: hiddenCount })}
              </Badge>
            </React.Fragment>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {columns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(isChecked) => column.toggleVisibility(isChecked)}
            // Le menu reste ouvert : on règle plusieurs colonnes d'affilée.
            onSelect={(event) => event.preventDefault()}
          >
            {getColumnLabel(column)}
          </DropdownMenuCheckboxItem>
        ))}
        {hiddenCount > 0 ? (
          <React.Fragment>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center"
              onSelect={() =>
                columns.forEach((column) => column.toggleVisibility(true))
              }
            >
              {t("dataTable.showAllColumns")}
            </DropdownMenuItem>
          </React.Fragment>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default DataTableColumnVisibility;
